import { useMemo, useRef, useState } from "react";
import {
  ABILITIES,
  mdmToArenaMap,
  parseMdm,
  serializeMdm,
  validateMdm
} from "@marshmallow-duel/simulation";

import { abilityName } from "../ability-names";
import { ScaledStage } from "./ScaledStage";

import type {
  ArenaMap,
  Language,
  MdmMap,
  MdmObject,
  MdmObjectType,
  PlayerCount,
  PlayerSlot
} from "@marshmallow-duel/simulation";
import type { TranslationKey } from "../i18n";

interface EditorObject extends MdmObject {
  readonly editorId: number;
}

type EditorTool = MdmObjectType | `player-${PlayerSlot}`;

interface MapEditorProps {
  readonly language: Language;
  readonly t: (key: TranslationKey) => string;
  readonly onBack: () => void;
  readonly onTest: (map: ArenaMap) => void;
  readonly onPublish?: (map: MdmMap, description: string) => Promise<void>;
}

interface ToolDefinition {
  readonly type: MdmObjectType;
  readonly sprite: string;
  readonly label: { readonly cz: string; readonly en: string };
}

const TOOLS: readonly ToolDefinition[] = [
  { type: 1, sprite: "spr_mapeditor_button_wall", label: { cz: "Zeď", en: "Wall" } },
  { type: 2, sprite: "spr_mapeditor_button_leftwall", label: { cz: "Levá zeď", en: "Left wall" } },
  { type: 3, sprite: "spr_mapeditor_button_rightwall", label: { cz: "Pravá zeď", en: "Right wall" } },
  { type: 4, sprite: "spr_mapeditor_button_bottomwall", label: { cz: "Spodní zeď", en: "Bottom wall" } },
  { type: 5, sprite: "spr_mapeditor_button_teleport", label: { cz: "Teleport", en: "Teleport" } },
  { type: 6, sprite: "spr_mapeditor_button_kicker", label: { cz: "Odražeč", en: "Kicker" } },
  { type: 7, sprite: "spr_mapeditor_button_hkickerleft", label: { cz: "Odražeč vlevo", en: "Left kicker" } },
  { type: 8, sprite: "spr_mapeditor_button_hkickerright", label: { cz: "Odražeč vpravo", en: "Right kicker" } },
  { type: 9, sprite: "spr_mapeditor_button_field", label: { cz: "Pole", en: "Field" } },
  { type: 10, sprite: "spr_mapeditor_button_kickerfield", label: { cz: "Odrazové pole", en: "Kicker field" } },
  { type: 11, sprite: "spr_mapeditor_button_ropetop", label: { cz: "Vrchol lana", en: "Rope top" } },
  { type: 12, sprite: "spr_mapeditor_button_rope", label: { cz: "Lano", en: "Rope" } }
] as const;

const OBJECT_SPRITES: Readonly<Record<MdmObjectType, string>> = {
  1: "spr_mapeditor_wall",
  2: "spr_mapeditor_leftwall",
  3: "spr_mapeditor_rightwall",
  4: "spr_mapeditor_bottomwall",
  5: "spr_mapeditor_teleport",
  6: "spr_mapeditor_kicker",
  7: "spr_mapeditor_hkickerleft",
  8: "spr_mapeditor_hkickerright",
  9: "spr_mapeditor_field",
  10: "spr_mapeditor_kickerfield",
  11: "spr_mapeditor_ropetop",
  12: "spr_mapeditor_rope"
};

const PLAYER_COLORS: Readonly<Record<PlayerSlot, string>> = {
  1: "#ff3030",
  2: "#3080ff",
  3: "#30ff30",
  4: "#ffff30"
};

const spawnPositions: Readonly<Record<PlayerSlot, { readonly x: number; readonly y: number }>> = {
  1: { x: 80, y: 496 },
  2: { x: 720, y: 496 },
  3: { x: 272, y: 496 },
  4: { x: 496, y: 496 }
};

const platformCenters: Readonly<Record<PlayerSlot, number>> = { 1: 64, 2: 704, 3: 256, 4: 480 };

const createBlankMap = (playerCount: PlayerCount, language: Language): MdmMap => {
  const players: Partial<Record<PlayerSlot, { x: number; y: number }>> = {};
  const objects: MdmObject[] = [];
  for (let slot = 1; slot <= playerCount; slot += 1) {
    const playerSlot = slot as PlayerSlot;
    players[playerSlot] = { ...spawnPositions[playerSlot] };
    const center = platformCenters[playerSlot];
    for (const offset of [-32, 0, 32]) objects.push({ type: 1, x: center + offset, y: 528 });
  }
  return {
    version: "1.2",
    name: language === "cz" ? "Nová mapa" : "New map",
    players,
    objects,
    fixedAbilityIds: null
  };
};

const withEditorIds = (objects: readonly MdmObject[]): EditorObject[] => objects.map((object, index) => ({ ...object, editorId: index + 1 }));

const playerCountOf = (map: MdmMap): PlayerCount => Math.max(2, Math.min(4, Object.keys(map.players).length)) as PlayerCount;

const safeFileName = (name: string): string => `${name.trim().replace(/[^a-z0-9á-ž_-]+/gi, "_").replace(/^_+|_+$/g, "") || "map"}.mdm`;

export const MapEditor = ({ language, t, onBack, onTest, onPublish }: MapEditorProps) => {
  const firstMap = useMemo(() => createBlankMap(2, language), []);
  const [name, setName] = useState(firstMap.name);
  const [description, setDescription] = useState("");
  const [players, setPlayers] = useState<MdmMap["players"]>(firstMap.players);
  const [objects, setObjects] = useState<EditorObject[]>(withEditorIds(firstMap.objects));
  const [fixedAbilityIds, setFixedAbilityIds] = useState<readonly number[] | null>(null);
  const [tool, setTool] = useState<EditorTool>(1);
  const [showAbilities, setShowAbilities] = useState(false);
  const [showMetadata, setShowMetadata] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [pendingTeleport, setPendingTeleport] = useState<number | null>(null);
  const [message, setMessage] = useState("");
  const [publishing, setPublishing] = useState(false);
  const nextId = useRef(objects.length + 1);
  const inputRef = useRef<HTMLInputElement>(null);

  const currentMap = (): MdmMap => ({ version: "1.2", name: name.trim(), players, objects, fixedAbilityIds });

  const replaceMap = (map: MdmMap) => {
    setName(map.name);
    setPlayers(map.players);
    setObjects(withEditorIds(map.objects));
    setFixedAbilityIds(map.fixedAbilityIds);
    setPendingTeleport(null);
    nextId.current = map.objects.length + 1;
    setMessage(language === "cz" ? "Mapa byla načtena." : "Map loaded.");
  };

  const reset = () => replaceMap(createBlankMap(playerCountOf(currentMap()), language));

  const setPlayerCount = (count: PlayerCount) => {
    const next: Partial<Record<PlayerSlot, { x: number; y: number }>> = {};
    for (let slot = 1; slot <= count; slot += 1) {
      const playerSlot = slot as PlayerSlot;
      next[playerSlot] = players[playerSlot] ?? { ...spawnPositions[playerSlot] };
    }
    setPlayers(next);
  };

  const positionFromEvent = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const rawX = ((event.clientX - rect.left) / rect.width) * 800;
    const rawY = ((event.clientY - rect.top) / rect.height) * 600;
    return {
      x: Math.max(0, Math.min(784, Math.round(rawX / 16) * 16)),
      y: Math.max(48, Math.min(576, Math.round(rawY / 16) * 16))
    };
  };

  const place = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    const point = positionFromEvent(event);
    if (typeof tool === "string") {
      const slot = Number(tool.slice(-1)) as PlayerSlot;
      setPlayers((current) => ({ ...current, [slot]: { x: point.x, y: point.y - 32 } }));
      return;
    }
    if (pendingTeleport !== null) {
      setObjects((current) => current.map((object) => object.editorId === pendingTeleport ? { ...object, targetX: point.x, targetY: point.y - 32 } : object));
      setPendingTeleport(null);
      setMessage("");
      return;
    }
    const editorId = nextId.current++;
    const object: EditorObject = { type: tool, x: point.x, y: point.y - 32, editorId };
    if (tool === 5) {
      setObjects((current) => [...current, { ...object, targetX: point.x, targetY: point.y - 32 }]);
      setPendingTeleport(editorId);
      setMessage(language === "cz" ? "Kliknutím zvolte cíl teleportu." : "Click to choose the teleport target.");
    } else {
      setObjects((current) => [...current, object]);
    }
  };

  const removeAt = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    const point = positionFromEvent(event);
    let nearest: EditorObject | undefined;
    let nearestDistance = 40;
    for (const object of objects) {
      const distance = Math.hypot(object.x - point.x, object.y + 32 - point.y);
      if (distance < nearestDistance) {
        nearest = object;
        nearestDistance = distance;
      }
    }
    if (nearest) {
      setObjects((current) => current.filter(({ editorId }) => editorId !== nearest?.editorId));
      if (pendingTeleport === nearest.editorId) setPendingTeleport(null);
    }
  };

  const validatedMap = (): MdmMap | null => {
    const map = currentMap();
    const issues = validateMdm(map);
    if (issues.length > 0) {
      setMessage(issues.map(({ message: issue }) => issue).join(" "));
      return null;
    }
    setMessage("");
    return map;
  };

  const test = () => {
    const map = validatedMap();
    if (map) onTest(mdmToArenaMap(map, `editor-${Date.now()}`));
  };

  const download = () => {
    const map = validatedMap();
    if (!map) return;
    const url = URL.createObjectURL(new Blob([serializeMdm(map)], { type: "text/plain;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = safeFileName(map.name);
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const loadFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      const map = parseMdm(await file.text());
      const issues = validateMdm(map);
      if (issues.length > 0) throw new Error(issues.map(({ message: issue }) => issue).join(" "));
      replaceMap(map);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : (language === "cz" ? "Mapu nelze načíst." : "The map could not be loaded."));
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const publish = async () => {
    const map = validatedMap();
    if (!map || !onPublish) return;
    setPublishing(true);
    try {
      await onPublish(map, description);
      setMessage(language === "cz" ? "Mapa je uložená v online knihovně." : "Map saved to the online library.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : (language === "cz" ? "Mapu se nepodařilo publikovat." : "The map could not be published."));
    } finally {
      setPublishing(false);
    }
  };

  const selectedAbilities = new Set(fixedAbilityIds ?? []);
  const toggleAbility = (id: number) => {
    const next = new Set(selectedAbilities);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setFixedAbilityIds([...next].sort((left, right) => left - right));
  };

  return (
    <ScaledStage className="editor-stage">
      <div
        className={`editor-workspace${showGrid ? " editor-workspace--grid" : ""}`}
        onMouseDown={place}
        onContextMenu={removeAt}
        role="application"
        aria-label={t("editor.canvas")}
      >
        {objects.map((object) => (
          <img
            key={object.editorId}
            className="editor-object"
            src={`/assets/original/sprites/${OBJECT_SPRITES[object.type]}/0.png`}
            style={{ left: object.x, top: object.y + 32 }}
            title={`${object.type}: ${object.x}, ${object.y}`}
            alt=""
            draggable={false}
          />
        ))}
        {objects.filter((object) => object.type === 5).map((object) => (
          <img
            key={`target-${object.editorId}`}
            className="editor-teleport-target"
            src="/assets/original/sprites/spr_mapeditor_teleport_point/0.png"
            style={{ left: object.targetX ?? object.x, top: (object.targetY ?? object.y) + 32 }}
            alt=""
            draggable={false}
          />
        ))}
        {Object.entries(players).map(([slot, position]) => position && (
          <button
            key={slot}
            type="button"
            className="editor-player"
            style={{ left: position.x, top: position.y + 32, borderColor: PLAYER_COLORS[Number(slot) as PlayerSlot] }}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={() => setTool(`player-${slot}` as EditorTool)}
            title={`${language === "cz" ? "Hráč" : "Player"} ${slot}`}
          >
            <img src="/assets/original/sprites/spr_player_1_standing/0.png" alt="" draggable={false} />
            <span style={{ color: PLAYER_COLORS[Number(slot) as PlayerSlot] }}>P{slot}</span>
          </button>
        ))}
      </div>

      <header className="editor-toolbar">
        <button type="button" onClick={reset}>{t("editor.new")}</button>
        <button type="button" onClick={() => inputRef.current?.click()}>{t("editor.load")}</button>
        <button type="button" onClick={download}>{t("editor.save")}</button>
        <button type="button" onClick={() => setShowMetadata((value) => !value)}>{t("editor.publish")}</button>
        <button type="button" onClick={test}>{t("editor.test")}</button>
        <button type="button" onClick={() => setShowMetadata((value) => !value)}>{t("editor.name")}</button>
        <button type="button" onClick={() => setShowAbilities(true)}>{t("editor.abilities")}</button>
        <input ref={inputRef} type="file" accept=".mdm,text/plain" hidden onChange={(event) => void loadFile(event.target.files?.[0])} />
      </header>

      <footer className="editor-toolbox">
        {TOOLS.map((definition) => (
          <button
            type="button"
            key={definition.type}
            className={tool === definition.type ? "active" : ""}
            onClick={() => { setTool(definition.type); setPendingTeleport(null); }}
            title={definition.label[language]}
          >
            <img src={`/assets/original/sprites/${definition.sprite}/0.png`} alt="" />
          </button>
        ))}
        {(Object.keys(players) as unknown as PlayerSlot[]).map((slot) => (
          <button type="button" key={slot} className={tool === `player-${slot}` ? "active editor-player-tool" : "editor-player-tool"} onClick={() => setTool(`player-${slot}`)} title={`${language === "cz" ? "Hráč" : "Player"} ${slot}`} style={{ color: PLAYER_COLORS[slot] }}>P{slot}</button>
        ))}
      </footer>

      <label className="editor-grid-toggle">
        <input type="checkbox" checked={showGrid} onChange={(event) => setShowGrid(event.target.checked)} />
        {t("editor.grid")}
      </label>
      <button type="button" className="editor-back" onClick={onBack}>{t("common.back")}</button>
      {message && <div className="editor-status">{message}</div>}

      {showMetadata && (
        <section className="editor-popover editor-metadata">
          <label>{t("editor.name")}<input value={name} maxLength={48} onChange={(event) => setName(event.target.value)} /></label>
          <label>{t("editor.players")}
            <select value={playerCountOf(currentMap())} onChange={(event) => setPlayerCount(Number(event.target.value) as PlayerCount)}>
              <option value={2}>2</option><option value={3}>3</option><option value={4}>4</option>
            </select>
          </label>
          <label>{t("editor.description")}<textarea value={description} maxLength={500} onChange={(event) => setDescription(event.target.value)} /></label>
          <div>
            <button type="button" onClick={() => setShowMetadata(false)}>{t("common.back")}</button>
            {onPublish && <button type="button" disabled={publishing} onClick={() => void publish()}>{publishing ? t("editor.publishing") : t("editor.publish")}</button>}
          </div>
        </section>
      )}

      {showAbilities && (
        <section className="editor-popover editor-abilities">
          <h2>{t("editor.abilities")}</h2>
          <label className="editor-global-abilities">
            <input type="checkbox" checked={fixedAbilityIds === null} onChange={(event) => setFixedAbilityIds(event.target.checked ? null : [])} />
            {t("editor.globalAbilities")}
          </label>
          <div className="editor-ability-grid">
            {ABILITIES.map((ability) => (
              <label key={ability.id}>
                <input type="checkbox" disabled={fixedAbilityIds === null} checked={selectedAbilities.has(ability.id)} onChange={() => toggleAbility(ability.id)} />
                <img src={`/assets/original/sprites/${ability.icon}/0.png`} alt="" />
                <span>{abilityName(language, ability.id)}</span>
              </label>
            ))}
          </div>
          <button type="button" onClick={() => setShowAbilities(false)}>{t("common.save")}</button>
        </section>
      )}
    </ScaledStage>
  );
};
